// packages/widget/src/telemetry.ts
import type { ConnectionStatus } from "@microsoft/voice-widget-core-client";

// ─── Types ───────────────────────────────────────────────────────────────────

export type TelemetryAttemptStatus = "active" | "superseded" | "stopped" | "destroyed";
export type TelemetryStage = "grant" | "negotiate" | "provider" | "broker";
export type BrokerEndpoint = "session" | "update" | "end";

export interface TelemetryEnvelope {
  timestamp: number;
  widgetInstanceId: string;
  correlationId: string;
  attemptStatus: TelemetryAttemptStatus;
  sessionId?: string;
}

export type VoiceAgentTelemetryEvent =
  | (TelemetryEnvelope & { name: "attempt_started"; data: { provider?: string } })
  | (TelemetryEnvelope & {
      name: "grant_resolved";
      data: { source: "getSession" | "synthetic" };
    })
  | (TelemetryEnvelope & {
      name: "connection_established";
      data: { transport?: string };
    })
  | (TelemetryEnvelope & { name: "broker_request"; data: { endpoint: BrokerEndpoint } })
  | (TelemetryEnvelope & {
      name: "broker_response";
      data: {
        endpoint: BrokerEndpoint;
        ok: boolean;
        status?: number;
        durationMs: number;
        errorCode?: string;
        correlationMismatch?: true;
      };
    })
  | (TelemetryEnvelope & {
      name: "session_id_resolved";
      data: { source: "grant" | "negotiate" | "provider"; previousSessionId?: string };
    })
  | (TelemetryEnvelope & { name: "status_changed"; data: { status: ConnectionStatus } })
  | (TelemetryEnvelope & { name: "session_disconnected"; data: { reason?: string } })
  | (TelemetryEnvelope & {
      name: "attempt_failed";
      data: { code?: string; stage: TelemetryStage; recoverable: boolean };
    })
  | (TelemetryEnvelope & { name: "attempt_retired"; data?: undefined });

export interface AttemptContext {
  gen: number;
  correlationId: string;
  retiredAs?: Exclude<TelemetryAttemptStatus, "active">;
  failureEmitted?: boolean;
  connectionEmitted?: boolean;
  sessionId?: string;
}

// ─── ID Generation ───────────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback: construct UUIDv4 from getRandomValues
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function isUuid(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

// ─── Validators ──────────────────────────────────────────────────────────────

// Starts with alphanumeric, followed by 0-63 more chars from [A-Za-z0-9_.:-]
// Total max 64 chars.
const TOKEN_RE = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;

// Printable ASCII (0x21-0x7E, no space), 1-128 chars
const SESSION_ID_RE = /^[\x21-\x7E]{1,128}$/;

const VALID_STATUSES: ReadonlySet<string> = new Set([
  "idle",
  "connecting",
  "connected",
  "disconnected",
  "error",
]);

export function token(v: unknown): string | undefined {
  return typeof v === "string" && TOKEN_RE.test(v) ? v : undefined;
}

export function validateSessionId(v: unknown): string | undefined {
  return typeof v === "string" && SESSION_ID_RE.test(v) ? v : undefined;
}

export function validateStatus(v: unknown): ConnectionStatus | undefined {
  return typeof v === "string" && VALID_STATUSES.has(v) ? (v as ConnectionStatus) : undefined;
}

export function normalizeBool(v: unknown): boolean {
  return v === true;
}

// ─── Emitter ─────────────────────────────────────────────────────────────────

export interface TelemetryEmitter {
  emit(
    ctx: AttemptContext,
    name: VoiceAgentTelemetryEvent["name"],
    data: unknown,
    generation: number,
  ): void;
  createAttempt(gen: number): AttemptContext;
  retireAttempt(
    reason: Exclude<TelemetryAttemptStatus, "active">,
    generation: number,
  ): AttemptContext | undefined;
  statusFor(ctx: AttemptContext, generation: number): TelemetryAttemptStatus;
  emitFailureOnce(
    ctx: AttemptContext,
    stage: TelemetryStage,
    err: { code?: string; recoverable?: unknown } | undefined,
    generation: number,
  ): void;
  emitConnectionOnce(ctx: AttemptContext, transport: unknown, generation: number): void;
}

export function createTelemetryEmitter(opts: {
  widgetInstanceId: string;
  onTelemetryEvent?: (e: VoiceAgentTelemetryEvent) => void;
  telemetryConsole?: boolean;
}): TelemetryEmitter {
  let currentAttempt: AttemptContext | undefined;
  // Per-instance latch: once onTelemetryEvent throws, it is silenced for all future calls.
  let sinkErrored = false;

  function statusFor(ctx: AttemptContext, generation: number): TelemetryAttemptStatus {
    // retiredAs is authoritative — check it first before generation comparison
    if (ctx.retiredAs !== undefined) return ctx.retiredAs;
    return ctx.gen === generation ? "active" : "superseded";
  }

  function deliver(event: VoiceAgentTelemetryEvent): void {
    if (opts.onTelemetryEvent !== undefined && !sinkErrored) {
      try {
        opts.onTelemetryEvent(event);
      } catch (err) {
        // Report the first throw, then stop invoking the sink for this instance: telemetry
        // emits on the session's hot path, so a permanently broken sink is dropped rather
        // than retried on every event. Deliberately stricter than `onRawEvent` and the Node
        // broker hook, which keep invoking a throwing handler and suppress only the logs.
        // Full silence is not an option either — it leaves the consumer no signal at all.
        sinkErrored = true;
        // eslint-disable-next-line no-console
        console.error(
          "[voice-agent] onTelemetryEvent handler threw; it is isolated from telemetry and " +
            "will not be called again for this core instance.",
          err,
        );
      }
    }
    if (opts.telemetryConsole === true) {
      // eslint-disable-next-line no-console
      console.debug("[voice-agent:telemetry]", event.name, event);
    }
  }

  function emit(
    ctx: AttemptContext,
    name: VoiceAgentTelemetryEvent["name"],
    data: unknown,
    generation: number,
  ): void {
    const sessionId = validateSessionId(ctx.sessionId);
    const envelope: TelemetryEnvelope = {
      timestamp: Date.now(),
      widgetInstanceId: opts.widgetInstanceId,
      correlationId: ctx.correlationId,
      attemptStatus: statusFor(ctx, generation),
      ...(sessionId !== undefined ? { sessionId } : {}),
    };
    deliver({ ...envelope, name, data } as VoiceAgentTelemetryEvent);
  }

  function createAttempt(gen: number): AttemptContext {
    const ctx: AttemptContext = { gen, correlationId: generateId() };
    currentAttempt = ctx;
    return ctx;
  }

  function retireAttempt(
    reason: Exclude<TelemetryAttemptStatus, "active">,
    generation: number,
  ): AttemptContext | undefined {
    const ctx = currentAttempt;
    if (ctx === undefined) return undefined;
    // Write-once: only set retiredAs on first retirement call
    if (ctx.retiredAs === undefined) {
      ctx.retiredAs = reason;
      emit(ctx, "attempt_retired", undefined, generation);
    }
    currentAttempt = undefined;
    return ctx;
  }

  function emitFailureOnce(
    ctx: AttemptContext,
    stage: TelemetryStage,
    err: { code?: string; recoverable?: unknown } | undefined,
    generation: number,
  ): void {
    if (ctx.failureEmitted === true) return;
    ctx.failureEmitted = true;
    emit(
      ctx,
      "attempt_failed",
      {
        stage,
        code: token(err?.code),
        recoverable: normalizeBool(err?.recoverable),
      },
      generation,
    );
  }

  function emitConnectionOnce(ctx: AttemptContext, transport: unknown, generation: number): void {
    // Per-attempt latch, mirroring emitFailureOnce. An adapter is free to call onConnect
    // more than once for one session — after an in-place recovery, say — but the event's
    // documented contract is one per successful connection, so extra calls are dropped
    // rather than allowed to double-count every dashboard built on it.
    if (ctx.connectionEmitted === true) return;
    ctx.connectionEmitted = true;
    emit(ctx, "connection_established", { transport: token(transport) }, generation);
  }

  return { emit, createAttempt, retireAttempt, statusFor, emitFailureOnce, emitConnectionOnce };
}
